/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {
    Fragment,
    useCallback,
} from "react";
import {useSelector} from "react-redux";

import {useSortedData} from "../../../Hooks/ListHooks";
import {MutableList} from "./MutableList";
import {service as FunderService} from "../../../Services/FunderService";
import {useAutoCompleteOptionsFilterCallback} from "../../../Hooks/useAutoCompleteOptionsFilterCallback";

// Generally rendered in ChildFieldEditor around ./../../../Models/Core/FieldEditors/ChildrenFieldEditor.jsx:47
export const ProjectFunderList = (props) => {
    const {
        AddItem,
        AdditionalTypeProps,
        UpdateItem = (item) => {},
        Data,
        sortKey,
    } = props;
    
    const { projectContextNodeId } = useSelector(state => state.contextState.context);
    
    // useSortedData can return a non-array (null, or an empty `{}` from the API
    // when a project has no funders yet) — coerce to an array so the options
    // filter callback's `.map` and `onValidateItem`'s `.some` don't blow up.
    const sortedData = useSortedData(Data, sortKey);
    const data = Array.isArray(sortedData) ? sortedData : [];

    const filter = useAutoCompleteOptionsFilterCallback(
        FunderService.PrimaryKeyProperty,
        data
    );
    
    const onValidateItem = async (inputValue) => {
        
        // if we already have an unsaved Funder with this name in the list, then don't allow it
        if(data.some(existingItem => existingItem?.Funder?.name?.toLowerCase().trim() === inputValue.toLowerCase().trim())) {
            return false;
        }
        
        // if we already have a saved Funder with this name in the list, then don't allow it either
        const funders = (await FunderService.Search(inputValue))?.result;
        return funders?.every(funder => (funder.name ?? '').toLowerCase().trim() !== inputValue.toLowerCase().trim() );
    }
    
    const addProjectFunder = () => {
        AddItem({
            projectFunderId: null,
            projectContextNodeId,
        });
    }
    
    const customValidator = (validation, editorGuid) => {
        
        // override validation
        console.log(validation);
        const funderIdValidation = validation['funderId'];
        if(funderIdValidation) {
            validation['funderId'] = {
                className: '',
                message: null,
                valid: true,
            }
        }
        
        AdditionalTypeProps.OnValidationUpdate(validation, editorGuid);
    };
    
    const onUpdateModel = useCallback((projectFunder) => {
        // enforce the projectContextNodeId from the context
        const updatedProjectFunder = {
            ...projectFunder,
            projectContextNodeId,
        };
        UpdateItem?.(updatedProjectFunder);
    }, [projectContextNodeId, UpdateItem]);
    
    const onUpdateItem = (key, value) => {
        console.log(key, value);
    }
    
    return <Fragment>
            
        <MutableList
            {...props}
            Data={data}
            
            // When the MutableList item is added or updated
            AddItem={addProjectFunder}
            UpdateItem={onUpdateItem}
            
            // TODO: what method gets called when we create a new guy
            
            AdditionalTypeProps={{
                // props passed down to the MutableList's Type component
                // which is GenericModelViewerListNode, which is a GenericModelViewer
                // which then passes down the CustomData to the 'ProjectFunderFieldEditor' specified in the ProjectFunder_trait.php
                // and ultimately addItem to ServiceDrivenAutoCompleteFieldEditor
                ...AdditionalTypeProps,
                
                OnValidationUpdate: customValidator,
                CustomData: {
                    ...AdditionalTypeProps.CustomData,
                    OnUpdateModel: onUpdateModel,
                    onValidateItem,
                    filter,
                },
            }}
        />
    </Fragment>
}
